import type { Id } from '@e-socna/data-layer';

interface FileCategoryLike {
  readonly _id: Id;
  readonly parentCategoryId?: Id | null;
}

export interface FileCategoryTreeNode<T extends FileCategoryLike> {
  readonly category: T;
  readonly children: readonly FileCategoryTreeNode<T>[];
}

// A category whose parent isn't in the given list (filtered out, e.g.
// technical, or genuinely absent) is treated as a root - it must still be
// reachable, just without its real ancestry.
export const buildFileCategoryTree = <T extends FileCategoryLike>(
  categories: readonly T[],
): readonly FileCategoryTreeNode<T>[] => {
  const categoryIds = new Set(categories.map((category) => category._id));
  const childrenByParentId = new Map<Id, T[]>();
  const roots: T[] = [];

  for (const category of categories) {
    const parentId = category.parentCategoryId;
    const hasVisibleParent = !!parentId && categoryIds.has(parentId);

    if (hasVisibleParent) {
      const siblings = childrenByParentId.get(parentId) ?? [];
      siblings.push(category);
      childrenByParentId.set(parentId, siblings);
    } else {
      roots.push(category);
    }
  }

  const buildNode = (category: T): FileCategoryTreeNode<T> => ({
    category,
    children: (childrenByParentId.get(category._id) ?? []).map(buildNode),
  });

  return roots.map(buildNode);
};

export const findFileCategoryAncestorIds = <T extends FileCategoryLike>(
  categories: readonly T[],
  id: Id,
): readonly Id[] => {
  const categoryById = new Map(
    categories.map((category) => [category._id, category]),
  );
  const ancestorIds: Id[] = [];

  let currentParentId = categoryById.get(id)?.parentCategoryId;
  while (currentParentId && categoryById.has(currentParentId)) {
    ancestorIds.push(currentParentId);
    currentParentId = categoryById.get(currentParentId)?.parentCategoryId;
  }

  return ancestorIds;
};

interface FileCategoryLabelLike extends FileCategoryLike {
  readonly label: string;
}

export interface FlattenedFileCategory<T extends FileCategoryLabelLike> {
  readonly category: T;
  readonly depth: number;
  readonly breadcrumbLabel: string;
}

export const flattenFileCategoryTree = <T extends FileCategoryLabelLike>(
  nodes: readonly FileCategoryTreeNode<T>[],
  parentBreadcrumb: readonly string[] = [],
): readonly FlattenedFileCategory<T>[] =>
  nodes.flatMap((node) => {
    const breadcrumb = [...parentBreadcrumb, node.category.label];

    return [
      {
        category: node.category,
        depth: parentBreadcrumb.length,
        breadcrumbLabel: breadcrumb.join(' › '),
      },
      ...flattenFileCategoryTree(node.children, breadcrumb),
    ];
  });
